// Session 27: a course's home page (/courses/:courseKey).
//
// Dispatches on the client's own switch, exactly as SeoRecommendations does:
//   On      the full course: outcomes, lessons with progress, the time
//           estimate, the prompt library and the Owner Kit
//   Preview the teaser: outcomes, the whole lesson list locked, and ONE free
//           sample lesson. Nothing is recorded in Preview
//   Off     not in the rail at all, so a deep link shows the placeholder
//
// Course content comes from src/courses; the database holds only what a person
// did. Progress is per person, which is what makes the time-saved number real.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import { useClientPath } from '../lib/clientLink.js';
import Placeholder from './Placeholder.jsx';
import { ASK_EMAIL, ASK_LINE, courseMode } from '../modules/index.js';
import { courseByKey, readyLessons, sampleLesson, progressFor } from '../courses/index.js';
import { SaveStatus, useSaveStatus } from '../components/SaveStatus.jsx';
import SectionHeading from '../components/HowItWorksPanel.jsx';

export default function Course() {
  const { courseKey } = useParams();
  const { entitlements } = useAuth();
  const course = courseByKey(courseKey);
  if (!course) return <Navigate to="/" replace />;
  const mode = courseMode(entitlements);
  if (mode === 'off') return <Placeholder />;
  return <CoursePage course={course} readOnly={mode === 'preview'} />;
}

function CoursePage({ course, readOnly }) {
  const { me, activeClient } = useAuth();
  const to = useClientPath();
  const [rows, setRows] = useState([]);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (readOnly || !me?.id) { setLoading(false); return; }
    const [{ data: p }, { data: r }] = await Promise.all([
      supabase.from('v_portal_course_progress').select('*').eq('course_key', course.key),
      supabase.from('v_portal_course_responses').select('*').eq('course_key', course.key),
    ]);
    setRows(p || []);
    setResponses(r || []);
    setLoading(false);
  }, [course.key, me?.id, readOnly]);

  useEffect(() => { load(); }, [load]);

  const prog = useMemo(() => progressFor(course, rows, me?.id), [course, rows, me?.id]);
  const ready = readyLessons(course);
  const sample = sampleLesson(course);
  const myResponses = (responses || []).filter((r) => r.user_id === me?.id);
  const courseComplete = prog.total > 0 && prog.doneCount === prog.total;

  return (
    <>
      <SectionHeading title={course.title} explainer="ai_courses" label="the courses" />
      <p className="lede">{course.blurb}</p>

      {readOnly && (
        <p className="banner banner-gate">
          This is a preview. {ASK_LINE}{' '}
          <a href={`mailto:${ASK_EMAIL}?subject=${encodeURIComponent(`${course.title} for ${activeClient?.name || 'our team'}`)}`}>
            Email {ASK_EMAIL}
          </a>
        </p>
      )}

      <section className="course-block">
        <h2>What you&rsquo;ll walk away with</h2>
        <ul className="course-list course-outcomes">
          {course.outcomes.map((o, i) => <li key={i}>{o}</li>)}
        </ul>
      </section>

      {!readOnly && !loading && prog.total > 0 && (
        <p className="course-progress">
          {prog.doneCount} of {prog.total} lesson{prog.total === 1 ? '' : 's'} finished
        </p>
      )}

      <section className="course-block">
        <h2>Lessons</h2>
        <ol className="course-lessons">
          {course.lessons.map((l) => {
            const isReady = l.status === 'ready';
            const open = isReady && (!readOnly || (sample && sample.key === l.key));
            const finished = !readOnly && prog.isDone(l.key);
            return (
              <li key={l.key} className={`course-lessonrow ${finished ? 'course-lessonrow-done' : ''}`}>
                <div className="course-lessonrow-main">
                  {open ? (
                    <Link to={to(`/courses/${course.key}/${l.key}`)}>{l.title}</Link>
                  ) : (
                    <span className="course-lesson-locked">{l.title}</span>
                  )}
                  {l.outcome && <p className="course-lesson-outcome">{l.outcome}</p>}
                </div>
                <div className="course-lessonrow-side">
                  {!isReady && <span className="chip course-chip">Coming soon</span>}
                  {isReady && finished && <span className="chip course-chip">Finished</span>}
                  {isReady && !finished && readOnly && sample && sample.key === l.key &&
                    <span className="chip course-chip">Free sample</span>}
                  {isReady && !finished && readOnly && (!sample || sample.key !== l.key) &&
                    <span className="chip course-chip">Locked</span>}
                  {isReady && <span className="course-lesson-mins">{l.minutes} min</span>}
                </div>
              </li>
            );
          })}
        </ol>
        {ready.length === 0 && (
          <div className="empty">
            <h2>We&rsquo;re writing this one</h2>
            <p>The lessons appear here as soon as they are ready.</p>
          </div>
        )}
      </section>

      {!readOnly && (
        <>
          <TimeEstimate course={course} responses={myResponses} courseComplete={courseComplete} onSaved={load} />
          <AdminTasks course={course} responses={myResponses} onSaved={load} />
          <p className="course-block">
            <Link className="btn" to={to('/courses/prompts')}>My prompt library</Link>
          </p>
          {/* Every portal user at the client sees this. There is no "buyer"
              in the data model, and the kit is reference copy rather than
              anything sensitive. Gating it to the person who paid would need a
              buyer flag on portal_users. */}
          <OwnerKit course={course} />
        </>
      )}
    </>
  );
}

/* --------------------------------------------------------- the two estimates */

// The before and after number. Asked at the start, asked again at the end, and
// the gap is the thing worth showing an owner.
function TimeEstimate({ course, responses, courseComplete, onSaved }) {
  const existing = responses.filter((r) => r.kind === 'time_estimate');
  const start = existing.find((r) => r.slot === 'start');
  const end = existing.find((r) => r.slot === 'end');
  const [hours, setHours] = useState('');
  const [saved, setSaved] = useState('');
  const save = useSaveStatus();

  // Ask for the start estimate until it exists; then only ask for the end once
  // every lesson is finished.
  const phase = !start ? 'start' : (courseComplete && !end ? 'end' : null);

  async function submit(e) {
    e.preventDefault();
    const n = Number(hours);
    if (!Number.isFinite(n) || n < 0) return save.fail('Enter a number of hours');
    save.start();
    const payload = phase === 'start'
      ? { when: 'start', hours_per_week: n }
      : { when: 'end', hours_per_week: n, hours_saved: Math.max(0, (start?.payload?.hours_per_week ?? n) - n) };
    const { error } = await supabase.rpc('portal_course_save_response', {
      p_course_key: course.key, p_lesson_key: null,
      p_kind: 'time_estimate', p_payload: payload,
      // Session 27b: 'start' and 'end' are two rows. They shared one slot
      // before, so saving the closing estimate destroyed the opening one and
      // the learner was asked the opening question again.
      p_slot: phase,
    });
    if (error) return save.fail(error.message);
    setSaved(phase);
    setHours('');
    save.ok();
    if (onSaved) onSaved();
  }

  if (!phase) {
    if (!start) return null;
    const a = start.payload?.hours_per_week;
    const b = end?.payload?.hours_per_week;
    return (
      <section className="course-block course-estimate">
        <h2>Your time</h2>
        <p>
          You started at {a} hour{a === 1 ? '' : 's'} a week on admin
          {b !== undefined ? `, and finished at ${b}.` : '. The closing question appears once you finish every lesson.'}
        </p>
      </section>
    );
  }

  return (
    <section className="course-block course-estimate">
      <h2>Your time</h2>
      <form onSubmit={submit}>
        <label htmlFor={`est-${course.key}-${phase}`}>
          {phase === 'start' ? course.timeEstimate.startQuestion : course.timeEstimate.endQuestion}
        </label>
        <div className="course-estimate-row">
          <input
            id={`est-${course.key}-${phase}`}
            className="course-input"
            type="number" min="0" step="0.5" inputMode="decimal"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            placeholder="hours a week"
          />
          <button type="submit" className="btn" disabled={hours === ''}>Save</button>
          <SaveStatus status={save.status} />
        </div>
      </form>
      {saved && <p className="course-muted">Thank you. That is the number we compare against later.</p>}
    </section>
  );
}

/* ----------------------------------------------- the admin tasks brainstorm */

// "Admin tasks I want gone." A list, not prose, because the items become the
// shortlist for what to automate next.
function AdminTasks({ course, responses, onSaved }) {
  const existing = responses.find((r) => r.kind === 'admin_tasks');
  const [items, setItems] = useState(() => existing?.payload?.items || []);
  const [draft, setDraft] = useState('');
  const save = useSaveStatus();

  useEffect(() => { setItems(existing?.payload?.items || []); }, [existing?.id]);

  async function persist(next) {
    setItems(next);
    save.start();
    const { error } = await supabase.rpc('portal_course_save_response', {
      p_course_key: course.key, p_lesson_key: null,
      p_kind: 'admin_tasks', p_payload: { items: next },
    });
    if (error) return save.fail(error.message);
    save.ok();
    if (onSaved) onSaved();
  }

  function add(e) {
    e.preventDefault();
    const t = draft.trim();
    if (!t) return;
    setDraft('');
    persist([...items, t]);
  }

  return (
    <section className="course-block">
      <h2>Admin tasks you want gone</h2>
      <p>Jot down the jobs you would hand over tomorrow if you could. You will come back to this list.</p>
      {items.length > 0 && (
        <ul className="course-list">
          {items.map((it, i) => (
            <li key={i}>
              {it}{' '}
              <button type="button" className="course-linkbtn"
                onClick={() => persist(items.filter((_, j) => j !== i))}>remove</button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={add} className="course-estimate-row">
        <input
          id={`tasks-${course.key}`}
          className="course-input" type="text" value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="e.g. writing up meeting notes"
        />
        <button type="submit" className="btn" disabled={!draft.trim()}>Add</button>
        <SaveStatus status={save.status} />
      </form>
    </section>
  );
}

/* ----------------------------------------------------------------- owner kit */

function OwnerKit({ course }) {
  if (!course.ownerKit) return null;
  return (
    <section className="course-block course-ownerkit">
      <h2>{course.ownerKit.title}</h2>
      <p>{course.ownerKit.blurb}</p>
      <div className="course-kit">
        {course.ownerKit.items.map((it) => (
          <div key={it.key} className="card course-kit-item">
            <p className="course-kit-title">{it.title}</p>
            <p>{it.body}</p>
          </div>
        ))}
      </div>
      <p className="course-muted">
        Ask Digital Lotus for these as editable documents.{' '}
        <a href={`mailto:${ASK_EMAIL}?subject=${encodeURIComponent(`Owner Kit: ${course.title}`)}`}>
          Email {ASK_EMAIL}
        </a>
      </p>
    </section>
  );
}
